import React, { useMemo, useState, useCallback } from "react";

/**
 * ErulesViewer
 * Visualizes the uploaded EASA-style JSON with nested toc/topic nodes.
 * - Expand/Collapse per section
 * - Global search (filters by heading, metadata values, or paragraph text)
 * - Robust to mixed arrays: [{heading}, {toc:[...]}, {topic:[{topic-metadata},{topic-content}]}]
 *
 * Example:
 *   import data from "./object.json";
 *   export default function App() { return <ErulesViewer data={data} />; }
 */
export default function ErulesViewer({ data }) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(() => new Set(["root"]));

  // Normalize root: the uploaded file’s top-level is { "toc": [ ... ] }
  const rootItems = Array.isArray(data?.toc) ? data.toc : [];

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    const all = new Set();
    const walk = (items, prefix = "node") => {
      all.add(prefix);
      items?.forEach((node, i) => {
        const id = `${prefix}-${i}`;
        all.add(id);
        if (node?.toc) walk(node.toc, `${id}-toc`);
        if (Array.isArray(node?.topic)) {
          // a topic has children only through "topic-content" -> no nested toc here
        }
      });
    };
    walk(rootItems, "root");
    setExpanded(all);
  }, [rootItems]);

  const collapseAll = useCallback(() => setExpanded(new Set(["root"])), []);

  const q = query.trim().toLowerCase();
  const matchesText = useCallback(
    (text) => (q ? String(text || "").toLowerCase().includes(q) : true),
    [q]
  );

  // Filtering logic: a node is kept if itself matches, or a descendant matches
  const filterItems = useCallback(
    (items) =>
      items
        .map((node) => {
          // compute a shallow match score
          let selfMatch = false;
          let cloned = { ...node };

          if (node.heading) {
            selfMatch = matchesText(node.heading);
          }

          if (node.toc) {
            const filteredChildren = filterItems(node.toc);
            cloned.toc = filteredChildren;
            selfMatch = selfMatch || filteredChildren.length > 0;
          }

          if (Array.isArray(node.topic)) {
            // topic = [{ "topic-metadata": {...} }, { "topic-content": [ {para:...}, ... ] }]
            const meta = node.topic.find((t) => t["topic-metadata"])?.["topic-metadata"] || {};
            const content = node.topic.find((t) => t["topic-content"])?.["topic-content"] || [];

            const metaMatches =
              Object.entries(meta).some(([k, v]) => matchesText(k) || matchesText(v));

            const paraMatches = content.some((p) => matchesText(p?.para));

            selfMatch = selfMatch || metaMatches || paraMatches;

            cloned.topic = [
              { "topic-metadata": meta },
              { "topic-content": content.filter((p) => matchesText(p?.para) || !q) },
            ];
          }

          return selfMatch ? cloned : null;
        })
        .filter(Boolean),
    [matchesText, q]
  );

  const filteredRoot = useMemo(() => (q ? filterItems(rootItems) : rootItems), [q, filterItems, rootItems]);

  return (
    <div style={styles.app}>
      <header style={styles.header}>
        <h1 style={styles.title}>EASA eRules Viewer</h1>
        <div style={styles.controls}>
          <input
            aria-label="Search"
            placeholder="Search headings, metadata, or paragraphs…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={styles.search}
          />
          <button onClick={expandAll} style={styles.btn}>Expand all</button>
          <button onClick={collapseAll} style={styles.btnSecondary}>Collapse all</button>
        </div>
      </header>

      <SectionList
        items={filteredRoot}
        parentId="root"
        expanded={expanded}
        onToggle={toggle}
      />
    </div>
  );
}

/** Renders a heterogeneous list of nodes: {heading}, {toc:[]}, or {topic:[...]} */
function SectionList({ items, parentId, expanded, onToggle, depth = 0 }) {
  if (!items?.length) return null;
  return (
    <ul style={{ ...styles.ul, marginLeft: depth ? 16 : 0 }}>
      {items.map((node, index) => {
        const id = `${parentId}-${index}`;
        if (node.heading) {
          return (
            <li key={id} style={styles.li}>
              <HeadingRow
                id={id}
                title={node.heading}
                hasChildren={false}
                expanded={expanded.has(id)}
                onToggle={onToggle}
              />
            </li>
          );
        }

        if (node.toc) {
          const open = expanded.has(id);
          return (
            <li key={id} style={styles.li}>
              <HeadingRow
                id={id}
                title="Table of contents"
                hasChildren={true}
                expanded={open}
                onToggle={onToggle}
              />
              {open && (
                <SectionList
                  items={node.toc}
                  parentId={`${id}-toc`}
                  expanded={expanded}
                  onToggle={onToggle}
                  depth={depth + 1}
                />
              )}
            </li>
          );
        }

        if (Array.isArray(node.topic)) {
          const meta = node.topic.find((t) => t["topic-metadata"])?.["topic-metadata"] || {};
          const content = node.topic.find((t) => t["topic-content"])?.["topic-content"] || [];
          const title =
            meta["source-title"] ||
            meta["ERulesId"] ||
            meta["RegulatorySubject"] ||
            "Topic";
          const open = expanded.has(id);

          return (
            <li key={id} style={styles.li}>
              <HeadingRow
                id={id}
                title={title}
                subtitle={meta["TypeOfContent"]}
                hasChildren={true}
                expanded={open}
                onToggle={onToggle}
                badge={meta["RegulatorySource"]}
              />
              {open && (
                <div style={styles.topicBody}>
                  <MetaGrid meta={meta} />
                  <Paragraphs content={content} />
                </div>
              )}
            </li>
          );
        }

        // Unknown node shape: render JSON preview (defensive)
        return (
          <li key={id} style={styles.li}>
            <pre style={styles.pre}>{JSON.stringify(node, null, 2)}</pre>
          </li>
        );
      })}
    </ul>
  );
}

function HeadingRow({ id, title, subtitle, hasChildren, expanded, onToggle, badge }) {
  return (
    <div style={styles.row}>
      {hasChildren ? (
        <button
          onClick={() => onToggle(id)}
          aria-expanded={expanded}
          aria-controls={`${id}-panel`}
          style={styles.chevronBtn}
          title={expanded ? "Collapse" : "Expand"}
        >
          <span style={{ ...styles.chevron, transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}>▸</span>
        </button>
      ) : (
        <span style={{ width: 24, display: "inline-block" }} />
      )}
      <div style={styles.rowText}>
        <div style={styles.rowTitle}>
          <span>{title}</span>
          {badge ? <span style={styles.badge}>{badge}</span> : null}
        </div>
        {subtitle ? <div style={styles.subtitle}>{subtitle}</div> : null}
      </div>
    </div>
  );
}

function MetaGrid({ meta }) {
  const entries = Object.entries(meta || {}).filter(([, v]) => v !== "");
  if (!entries.length) return null;
  return (
    <div style={styles.metaGrid}>
      {entries.map(([k, v]) => (
        <div key={k} style={styles.metaItem}>
          <div style={styles.metaKey}>{k}</div>
          <div style={styles.metaVal}>{String(v)}</div>
        </div>
      ))}
    </div>
  );
}

function Paragraphs({ content }) {
  if (!content?.length) return null;
  return (
    <div style={styles.paras}>
      {content.map((p, i) => (
        <p key={i} style={styles.para}>{p?.para}</p>
      ))}
    </div>
  );
}

/* —————— Styles —————— */
const styles = {
  app: {
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    lineHeight: 1.4,
    color: "#0f172a",
    padding: "16px",
    background: "#f8fafc",
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
  },
  title: { margin: 0, fontSize: 20, fontWeight: 700 },
  controls: { display: "flex", gap: 8, alignItems: "center" },
  search: {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    minWidth: 320,
    outline: "none",
  },
  btn: {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #0ea5e9",
    background: "#0ea5e9",
    color: "white",
    cursor: "pointer",
  },
  btnSecondary: {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    background: "white",
    color: "#0f172a",
    cursor: "pointer",
  },
  ul: { listStyle: "none", paddingLeft: 0, margin: 0 },
  li: {
    background: "white",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    marginBottom: 10,
    overflow: "hidden",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    background: "#f1f5f9",
    borderBottom: "1px solid #e2e8f0",
  },
  chevronBtn: {
    width: 24,
    height: 24,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
  },
  chevron: { display: "inline-block", transition: "transform 120ms ease" },
  rowText: { display: "grid", gap: 2 },
  rowTitle: { display: "flex", alignItems: "center", gap: 8, fontWeight: 600 },
  badge: {
    fontSize: 12,
    background: "#e0f2fe",
    color: "#0369a1",
    padding: "2px 6px",
    borderRadius: 999,
    border: "1px solid #bae6fd",
  },
  subtitle: { fontSize: 12, color: "#475569" },
  topicBody: { padding: 12, display: "grid", gap: 12 },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 8,
  },
  metaItem: {
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: 8,
    background: "#fafafa",
  },
  metaKey: { fontSize: 12, color: "#64748b", marginBottom: 4 },
  metaVal: { whiteSpace: "pre-wrap" },
  paras: { display: "grid", gap: 8 },
  para: { margin: 0 },
  pre: {
    margin: 0,
    padding: 12,
    background: "#0b1020",
    color: "#e2e8f0",
    overflowX: "auto",
  },
};
